/**
 * GPT-Live client delegation metadata is an address, never task text.
 *
 * Platform-neutral: no DOM, no React, no `structuredClone`, no `crypto`. The
 * Desk (apps/web) and Pocket (apps/mobile) both fold over this one module so
 * the two clients cannot drift into different delegation semantics.
 */
import {
  LIVE_HANDOFF_DIALOGUE_MAX_CHARS,
  LIVE_HANDOFF_DIALOGUE_MAX_ROWS,
  type LiveHandoff,
} from "@cleo/contracts";
export interface LiveTranscriptFragment {
  readonly eventId: string;
  readonly speaker: "user" | "assistant";
  readonly text: string;
  readonly startMs: number;
  readonly endMs: number;
}
export interface LiveClientDelegation {
  readonly id: string;
  readonly eventId: string;
  readonly offsetMs: number;
}
export interface LiveDelegationSnapshot {
  readonly delegation: LiveClientDelegation;
  readonly fragments: readonly LiveTranscriptFragment[];
  readonly newInputEventIds: readonly string[];
  readonly uncertaintyNote: string;
}
export interface LiveMediaCallbacks {
  readonly onTranscript?: (fragment: LiveTranscriptFragment) => void;
  readonly onDelegation?: (delegation: LiveClientDelegation) => void;
}
/**
 * A spoken or silent append to the live model. `delegationId` is `null` only
 * for an append that answers no delegation of the current provider session:
 * the guide permits a null delegation ("must name a known client delegation
 * or be null"), which is how a replacement connection voices the result of a
 * job that a dropped connection delegated.
 */
export interface LiveMediaUpdate {
  readonly kind: "thinking" | "commentary";
  readonly delegationId: string | null;
  readonly content: string;
  readonly eventId: string;
}
export const LIVE_APPEND_MAX_BYTES = 500;
/** UTF-8 byte length without assuming a global `TextEncoder` (Hermes gained it late). */
export function utf8ByteLength(text: string): number {
  const encoder = (globalThis as { TextEncoder?: new () => { encode(s: string): Uint8Array } })
    .TextEncoder;
  if (encoder) return new encoder().encode(text).length;
  let bytes = 0;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) {
      bytes += 4;
      i++;
    } else bytes += 3;
  }
  return bytes;
}
const id = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0 && value.length <= 256 && !/[\s -]/u.test(value);
const time = (value: unknown): value is number =>
  Number.isSafeInteger(value) && (value as number) >= 0;
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
export function readLiveTranscriptEvent(value: unknown): LiveTranscriptFragment | null {
  if (
    !record(value) ||
    !["session.input_transcript.delta", "session.output_transcript.delta"].includes(
      String(value.type),
    ) ||
    !id(value.event_id) ||
    typeof value.delta !== "string" ||
    value.delta.length === 0 ||
    utf8ByteLength(value.delta) > 8_000 ||
    !time(value.start_ms) ||
    !time(value.end_ms) ||
    value.end_ms < value.start_ms
  )
    return null;
  return Object.freeze({
    eventId: value.event_id,
    speaker: value.type === "session.input_transcript.delta" ? "user" : "assistant",
    text: value.delta,
    startMs: value.start_ms,
    endMs: value.end_ms,
  });
}
export function readLiveDelegationEvent(value: unknown): LiveClientDelegation | null {
  if (
    !record(value) ||
    value.type !== "session.delegation.created" ||
    !id(value.event_id) ||
    !time(value.offset_ms) ||
    !record(value.delegation) ||
    value.delegation.target !== "client" ||
    !id(value.delegation.id)
  )
    return null;
  return Object.freeze({
    id: value.delegation.id,
    eventId: value.event_id,
    offsetMs: value.offset_ms,
  });
}
/** A UTF-8 byte limit, deliberately not an estimate of model tokens. */
export function liveUpdateEvent(update: LiveMediaUpdate) {
  if (
    !["thinking", "commentary"].includes(update.kind) ||
    (update.delegationId !== null && !id(update.delegationId)) ||
    !id(update.eventId) ||
    typeof update.content !== "string" ||
    !update.content.trim() ||
    utf8ByteLength(update.content) > LIVE_APPEND_MAX_BYTES
  )
    throw new Error(
      "Live updates require a known delegation, stable event ID and at most 500 UTF-8 bytes.",
    );
  return {
    type: `session.${update.kind}.append`,
    delegation_id: update.delegationId,
    content: update.content,
    event_id: update.eventId,
  };
}

/** Plain words for a hand-off the Desk refused or never started. */
export const LIVE_DELEGATION_NOT_STARTED_TEXT = "Cleo couldn't start that. Try again.";
/**
 * How long a hand-off the Desk has received may go without its turn starting
 * before the client stops saying "working". The Desk records a refused start
 * as an activity, but nothing wakes a reply reader that is waiting on a turn
 * that never begins, so a lost record would otherwise mean "working" until
 * the call ends. A turn normally starts within seconds, and a cold provider
 * session within tens of seconds; 90 s is well above both and still well
 * inside a Live call (at most 300 s), so the person hears the outcome. It
 * bounds only the start: once the turn starts, long work is never cut off.
 */
export const LIVE_DELEGATION_START_TIMEOUT_MS = 90_000;
/** The fields of a thread activity (`OrchestrationThreadActivity`) this check reads. */
export interface LiveThreadActivity {
  readonly kind: string;
  readonly turnId: string | null;
  readonly createdAt: string;
  readonly payload: unknown;
}
/**
 * W-a7a6b004: did the Desk refuse to start the turn for this delegated
 * message? The Desk records every refused or failed turn start as a
 * `provider.turn.start.failed` thread activity naming the message
 * (`payload.messageId`); one path that cannot name it is dated with the
 * message's own `createdAt` instead. That record is the only signal: no turn
 * exists, so the reply stream stays "waiting" and is never woken again.
 * Returns the Desk's detail (null when it gave none), or null when no failure
 * names this message.
 */
export function liveDelegationStartFailure(
  activities: ReadonlyArray<LiveThreadActivity>,
  message: { readonly messageId: string; readonly createdAt?: string | null },
): { readonly detail: string | null } | null {
  for (let i = activities.length - 1; i >= 0; i--) {
    const activity = activities[i]!;
    if (activity.kind !== "provider.turn.start.failed") continue;
    const payload = record(activity.payload) ? activity.payload : {};
    const named =
      typeof payload.messageId === "string"
        ? payload.messageId === message.messageId
        : activity.turnId === null &&
          typeof message.createdAt === "string" &&
          activity.createdAt === message.createdAt;
    if (!named) continue;
    return {
      detail:
        typeof payload.detail === "string" && payload.detail.trim() ? payload.detail.trim() : null,
    };
  }
  return null;
}

/** Bounds of the dialogue a replacement connection may carry as memory (never as access). */
export const LIVE_PRIOR_DIALOGUE_MAX_ROWS = 24;
export const LIVE_PRIOR_DIALOGUE_MAX_BYTES = 4_000;
export interface LivePriorDialogueRow {
  readonly speaker: "user" | "assistant";
  readonly text: string;
}
/**
 * Whole fragments folded into speaker rows, newest kept, trimmed to the
 * World's bounds. Fragments are never sliced mid-word to fit: a row that does
 * not fit is dropped whole from the oldest end.
 */
export function livePriorDialogueRows(
  fragments: readonly LiveTranscriptFragment[],
  bounds: { rows?: number; bytes?: number } = {},
): LivePriorDialogueRow[] {
  const maxRows = bounds.rows ?? LIVE_PRIOR_DIALOGUE_MAX_ROWS;
  const maxBytes = bounds.bytes ?? LIVE_PRIOR_DIALOGUE_MAX_BYTES;
  const rows: Array<{ speaker: "user" | "assistant"; text: string }> = [];
  for (const fragment of fragments) {
    const last = rows[rows.length - 1];
    if (last && last.speaker === fragment.speaker) last.text += fragment.text;
    else rows.push({ speaker: fragment.speaker, text: fragment.text });
  }
  const trimmed = rows
    .map((row) => ({ speaker: row.speaker, text: row.text.trim() }))
    .filter((row) => row.text.length > 0);
  const kept: LivePriorDialogueRow[] = [];
  let bytes = 0;
  for (let i = trimmed.length - 1; i >= 0 && kept.length < maxRows; i--) {
    const row = trimmed[i]!;
    const size = utf8ByteLength(row.text);
    if (bytes + size > maxBytes) break;
    bytes += size;
    kept.unshift(Object.freeze(row));
  }
  return kept;
}

export interface LiveHandoffParts {
  /** The person's new words, exactly: the message text, and all the Desk attests as theirs. */
  readonly text: string;
  readonly liveHandoff: LiveHandoff;
}
/**
 * W-ead01b93: the hand-off Pocket and the Desk send for a delegation. The
 * person's new words and the call's earlier dialogue (Cleo's lines, the
 * person's earlier words) are SEPARATE: the words are the message, attested
 * as the person's; the dialogue rides `liveHandoff` as context that is never
 * the person's request. Never one combined text. With "spoken request only"
 * the dialogue is empty. Whole observed fragments are evidence for a request,
 * never authoritative turn boundaries; the newest dialogue rows are kept
 * within the bounds (a UTF-8 byte budget also bounds the characters).
 */
export function liveHandoffParts(
  snapshot: LiveDelegationSnapshot,
  privacy: { readonly taskContext: "spoken-request-only" | "supporting-dialogue" } | null = null,
): LiveHandoffParts {
  const fresh = new Set(snapshot.newInputEventIds);
  const text = snapshot.fragments
    .filter((fragment) => fresh.has(fragment.eventId))
    .map((fragment) => fragment.text)
    .join("")
    .trim();
  const prior =
    privacy?.taskContext === "supporting-dialogue"
      ? snapshot.fragments.filter((fragment) => !fresh.has(fragment.eventId))
      : [];
  // Every row of the call before the new words, folded by speaker; then the
  // newest kept within the bounds. A newest row too long on its own is kept
  // shortened from its start (its last words are nearest the request), never
  // dropped to nothing; earlier rows left out are counted for the Desk to say.
  const all = livePriorDialogueRows(prior, {
    rows: Number.POSITIVE_INFINITY,
    bytes: Number.POSITIVE_INFINITY,
  });
  const kept: Array<LiveHandoff["supportingDialogue"][number]> = [];
  // UTF-8 bytes never undercount UTF-16 characters, so this keeps the contract's character bound.
  let budget = LIVE_HANDOFF_DIALOGUE_MAX_CHARS;
  for (let i = all.length - 1; i >= 0 && kept.length < LIVE_HANDOFF_DIALOGUE_MAX_ROWS; i--) {
    const row = all[i]!;
    const speaker = row.speaker === "user" ? ("person" as const) : ("cleo" as const);
    const size = utf8ByteLength(row.text);
    if (size <= budget) {
      budget -= size;
      kept.unshift(Object.freeze({ speaker, text: row.text }));
      continue;
    }
    if (kept.length === 0) {
      const tail = utf8Tail(row.text, budget - utf8ByteLength(SHORTENED_START));
      if (tail.length > 0) kept.unshift(Object.freeze({ speaker, text: SHORTENED_START + tail }));
    }
    break;
  }
  const omittedEarlierRows = all.length - kept.length;
  return {
    text,
    liveHandoff: {
      supportingDialogue: kept,
      ...(omittedEarlierRows > 0 ? { omittedEarlierRows } : {}),
    },
  };
}

/** The mark a shortened dialogue row begins with: its earlier words were left out. */
const SHORTENED_START = "… ";
/** The longest end of `text` within `maxBytes` UTF-8 bytes, never splitting a surrogate pair. */
function utf8Tail(text: string, maxBytes: number): string {
  if (maxBytes <= 0) return "";
  let start = text.length;
  let bytes = 0;
  while (start > 0) {
    const code = text.charCodeAt(start - 1);
    const pair =
      code >= 0xdc00 &&
      code <= 0xdfff &&
      start > 1 &&
      text.charCodeAt(start - 2) >= 0xd800 &&
      text.charCodeAt(start - 2) <= 0xdbff;
    const width = pair ? 2 : 1;
    const size = utf8ByteLength(text.slice(start - width, start));
    if (bytes + size > maxBytes) break;
    bytes += size;
    start -= width;
  }
  return text.slice(start).trimStart();
}

const UNCERTAINTY =
  "Voice transcript fragments may be incomplete or mistaken. The delegation offset is a causal marker, not a completed user turn or an exact word boundary. Interpret the request from this dialogue; clarify uncertain intent or action details. Later fragments cannot change this committed snapshot.";
/** Bounds of one call's retained draft history; a call's memory, not its meaning. */
export const LIVE_COLLECTOR_MAX_FRAGMENTS = 2_048;
export const LIVE_COLLECTOR_MAX_BYTES = 256_000;
/** Bounded per-call draft history. Only a delegation can freeze a request. */
export class LiveDelegationCollector {
  private readonly seenEvents = new Set<string>();
  private readonly snapshots = new Map<string, LiveDelegationSnapshot | null>();
  private fragments: Array<{ fragment: LiveTranscriptFragment; sequence: number; bytes: number }> =
    [];
  private sequence = 0;
  private readonly committedInputs = new Set<string>();
  private bytes = 0;
  private truncated = false;
  private uncommittedInputDropped = false;
  /** The whole fragments currently retained, oldest first. */
  history(): readonly LiveTranscriptFragment[] {
    return this.fragments.map(({ fragment }) => fragment);
  }
  /**
   * Carry a dropped connection's dialogue into this collector as committed
   * history: it can inform a later request as context, but no seeded user
   * speech can itself become a new request, so a replacement connection
   * cannot re-run the work the dropped one already delegated.
   */
  seed(prior: readonly LiveTranscriptFragment[]): number {
    let seeded = 0;
    for (const fragment of prior) {
      if (this.seenEvents.has(fragment.eventId) || this.seenEvents.size >= 4_096) continue;
      const checked = readLiveTranscriptEvent({
        type:
          fragment.speaker === "user"
            ? "session.input_transcript.delta"
            : "session.output_transcript.delta",
        event_id: fragment.eventId,
        delta: fragment.text,
        start_ms: 0,
        end_ms: 0,
      });
      if (!checked) continue;
      this.seenEvents.add(checked.eventId);
      const bytes = utf8ByteLength(checked.text);
      this.fragments.push({ fragment: checked, sequence: ++this.sequence, bytes });
      this.bytes += bytes;
      if (checked.speaker === "user") this.committedInputs.add(checked.eventId);
      seeded++;
    }
    this.evict();
    return seeded;
  }
  /**
   * Over budget, forget assistant speech and already-committed user speech
   * from the oldest end first; uncommitted user speech goes last, and only
   * then is a later request told the history overflowed. A five-minute call
   * fits comfortably inside the bounds; they guard memory, not meaning.
   */
  private evict() {
    while (
      this.fragments.length > LIVE_COLLECTOR_MAX_FRAGMENTS ||
      this.bytes > LIVE_COLLECTOR_MAX_BYTES
    ) {
      let index = this.fragments.findIndex(
        ({ fragment }) => fragment.speaker !== "user" || this.committedInputs.has(fragment.eventId),
      );
      if (index < 0) index = 0;
      const dropped = this.fragments.splice(index, 1)[0]!;
      this.bytes -= dropped.bytes;
      if (
        dropped.fragment.speaker === "user" &&
        !this.committedInputs.has(dropped.fragment.eventId)
      )
        this.uncommittedInputDropped = true;
      this.truncated = true;
    }
  }
  addTranscript(fragment: LiveTranscriptFragment): boolean {
    if (this.seenEvents.has(fragment.eventId)) return false;
    // Keep deduplication bounded without forgetting old IDs and replaying them.
    if (this.seenEvents.size >= 4_096) {
      this.truncated = true;
      if (fragment.speaker === "user" && fragment.text.length > 0)
        this.uncommittedInputDropped = true;
      return false;
    }
    const checked = readLiveTranscriptEvent({
      type:
        fragment.speaker === "user"
          ? "session.input_transcript.delta"
          : "session.output_transcript.delta",
      event_id: fragment.eventId,
      delta: fragment.text,
      start_ms: fragment.startMs,
      end_ms: fragment.endMs,
    });
    if (!checked || !["user", "assistant"].includes(fragment.speaker)) return false;
    this.seenEvents.add(checked.eventId);
    const bytes = utf8ByteLength(checked.text);
    this.fragments.push({ fragment: checked, sequence: ++this.sequence, bytes });
    this.bytes += bytes;
    this.evict();
    return true;
  }
  snapshotForDelegation(delegation: LiveClientDelegation): LiveDelegationSnapshot | null {
    if (this.snapshots.has(delegation.id)) return this.snapshots.get(delegation.id)!;
    if (this.uncommittedInputDropped)
      throw new Error(
        "Live bounded history overflow: uncommitted user speech was lost. Clarify the request before starting work.",
      );
    if (this.snapshots.size >= 64) return null;
    const checked = readLiveDelegationEvent({
      type: "session.delegation.created",
      event_id: delegation.eventId,
      offset_ms: delegation.offsetMs,
      delegation: { id: delegation.id, target: "client" },
    });
    if (!checked) return null;
    if (this.seenEvents.has(checked.eventId) || this.seenEvents.size >= 4_096) {
      this.snapshots.set(checked.id, null);
      return null;
    }
    this.seenEvents.add(checked.eventId);
    // An interval crossing the offset stays whole. Later-starting fragments
    // remain drafts; no interpolation or word slicing invents missing speech.
    const eligible = this.fragments.filter(({ fragment }) => fragment.startMs <= checked.offsetMs);
    const fresh = eligible.filter(
      ({ fragment }) => fragment.speaker === "user" && !this.committedInputs.has(fragment.eventId),
    );
    // Spacing belongs to the new utterance too, but cannot initiate work alone.
    if (!fresh.some(({ fragment }) => fragment.text.trim().length > 0)) {
      this.snapshots.set(checked.id, null);
      return null;
    }
    const firstFresh = fresh[0]!.sequence;
    const prior = eligible.filter(({ sequence }) => sequence < firstFresh).slice(-16);
    const selected = [...prior, ...eligible.filter(({ sequence }) => sequence >= firstFresh)].sort(
      (a, b) =>
        a.fragment.startMs - b.fragment.startMs ||
        a.fragment.endMs - b.fragment.endMs ||
        a.sequence - b.sequence,
    );
    const snapshot: LiveDelegationSnapshot = Object.freeze({
      delegation: checked,
      fragments: Object.freeze(selected.map(({ fragment }) => fragment)),
      newInputEventIds: Object.freeze(fresh.map(({ fragment }) => fragment.eventId)),
      uncertaintyNote:
        UNCERTAINTY +
        (this.truncated
          ? " Earlier transcript history exceeded the retention bound and is unavailable."
          : ""),
    });
    for (const { fragment } of fresh) this.committedInputs.add(fragment.eventId);
    this.snapshots.set(checked.id, snapshot);
    return snapshot;
  }
}
